import { useEffect, useState } from 'react';
import { Carousel, LucideIcon, type CarouselApi } from '@skin-seoul/design-system';
import { useCallbackRef } from '@skin-seoul/utils';
import { ReviewVideoThumbnail } from './ReviewVideoThumbnail';
import type { ReviewImage } from '..';

const CARD_PLAY_MARK_SIZE = 36;

interface ReviewCardImageCarouselProps {
  images: ReviewImage[];
  width: string;
  height: string;
  onImageClick: (image: ReviewImage, index: number) => void;
  /** Called when the carousel settles on a different image (SSD-5573). */
  onIndexChange?: (toIndex: number) => void;
}

export const ReviewCardImageCarousel = ({
  images,
  width,
  height,
  onImageClick,
  onIndexChange,
}: ReviewCardImageCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const handleIndexChange = useCallbackRef(onIndexChange);

  useEffect(() => {
    if (!api) {
      return;
    }

    // Embla's `select` only fires on an actual snap change, so a drag that springs back
    // to the same image reports nothing.
    const emitSelected = () => handleIndexChange(api.selectedScrollSnap());

    api.on('select', emitSelected);

    return () => {
      api.off('select', emitSelected);
    };
  }, [api, handleIndexChange]);

  return (
    <div style={{ width, height }} className="rounded-[8px] overflow-hidden shrink-0">
      <Carousel className="w-full h-full" opts={{ loop: true, align: 'center' }} setApi={setApi}>
        <Carousel.Content className="w-full h-full gap-[2px]">
          {images.map((image, index) => (
            <Carousel.Item key={image.id}>
              <div style={{ width, height }} className="bg-generic-0 flex items-center justify-center relative">
                {image.isVideo ? (
                  <ReviewVideoThumbnail
                    url={image.url}
                    playMarkSize={CARD_PLAY_MARK_SIZE}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => onImageClick(image, index)}
                  />
                ) : (
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => onImageClick(image, index)}
                  />
                )}
              </div>
            </Carousel.Item>
          ))}
        </Carousel.Content>
        {images.length > 1 && (
          <>
            <Carousel.Previous className="w-[32px] h-[32px] min-w-[32px] min-h-[32px] rounded-full bg-[rgba(247,247,249,0.2)] backdrop-blur-[8px] border-none hover:bg-[rgba(247,247,249,0.4)] left-[8px]">
              <LucideIcon name="ChevronLeft" width="24px" height="24px" className="text-neutral-50" />
            </Carousel.Previous>
            <Carousel.Next className="w-[32px] h-[32px] min-w-[32px] min-h-[32px] rounded-full bg-[rgba(247,247,249,0.2)] backdrop-blur-[8px] border-none hover:bg-[rgba(247,247,249,0.4)] right-[8px]">
              <LucideIcon name="ChevronRight" width="24px" height="24px" className="text-neutral-50" />
            </Carousel.Next>
            <Carousel.Dots
              className="absolute bottom-[8px] left-1/2 -translate-x-1/2 gap-[2px] p-2 rounded-[23px] bg-[rgba(247,247,249,0.2)] backdrop-blur-[16px]"
              renderDot={({
                onClick,
                isSelected,
                index,
              }: {
                onClick: () => void;
                isSelected: boolean;
                index: number;
              }) => (
                <div
                  key={index}
                  className={`h-1 rounded-[4px] cursor-pointer transition-all duration-200 ${isSelected ? 'w-5 bg-white' : 'w-1 bg-[#c4c4c4]'}`}
                  onClick={onClick}
                />
              )}
            />
          </>
        )}
      </Carousel>
    </div>
  );
};

export default ReviewCardImageCarousel;
